import React, { useCallback, useContext, useEffect } from 'react'
import Clickable from 'components/Common/Clickable'
import Pill from 'components/Luxkit/Pill'
import { TrendingDestination } from './constants'
import BodyText from 'components/Luxkit/Typography/BodyText'
import PillLoadingBox from 'components/Luxkit/PillLoadingBox'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/v2/OfferListEventsContext'
import { useInView } from 'react-intersection-observer'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import Image from 'components/Common/Image/Image'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'

const Section = styled.div`
  display: flex;
  grid-gap: ${rem(16)};
  width: ${rem(280)};

  ${mediaQueryUp.xs} {
    flex-direction: column;
    grid-gap: ${rem(12)};
    width: unset;
  }
`

const ImageContainer = styled.div`
  height: ${rem(85)};
  width: ${rem(85)};

  ${mediaQueryUp.xs} {
    height: ${rem(180)};
    width: ${rem(180)};
  }
`

interface Props {
  fetchingMaxSavings: boolean;
  index: number;
  savings?: number;
  trendingDestination: TrendingDestination;
}

function TrendingDestinationsTile(props: Props) {
  const { trendingDestination, savings, fetchingMaxSavings, index } = props
  const onEvent = useContext(OfferListEventsContext)
  const [inViewRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })

  useEffect(() => {
    if (inView && onEvent) {
      const params = {
        destination: trendingDestination.name,
        position: index,
        key: EventDataKey.TrendingImpression,
      }
      onEvent(OfferListEvents.impression, params)
    }
  }, [inView, index, onEvent, trendingDestination.name])

  const onClick = useCallback(() => {
    if (onEvent) {
      const params = {
        action: 'clicked',
        subject: trendingDestination.name,
        key: EventDataKey.ClientAction,
      }
      onEvent(OfferListEvents.clientAction, params)
    }
  }, [onEvent, trendingDestination.name])

  return (
    <div ref={inViewRef}>
      <Clickable href={trendingDestination.url} onClick={onClick}>
        <Section>
          <ImageContainer>
            <Image fit="center" withPlaceholder width={180} height={180} dpr={2} aspectRatio="1:1" id={trendingDestination.imageId} />
          </ImageContainer>
          <Group direction="vertical" gap={4} verticalAlign="center">
            <BodyText variant="large" weight="bold">{trendingDestination.name}</BodyText>
            {fetchingMaxSavings && (
              <PillLoadingBox width={96} />
            )}
            {savings && (
              <div>
                <Pill kind="primary" variant="success">Save up to {savings}%</Pill>
              </div>
            )}
          </Group>
        </Section>
      </Clickable>
    </div>
  )
}

export default TrendingDestinationsTile
